import { actions } from '../../../reducers/assignment.reducer'
import _ from 'lodash'

/**
 * Handler for optimistic deletion of patterns
 * @param {Object} param0 Query parameters
 * @param {Object} param1 API utilities
 */
export const handleDeletePatternOnQueryStarted = async ({ patternId }, { dispatch, getState, queryFulfilled }) => {
    // Get current data
    const { assignment } = getState();
    const currentData = assignment.descriptionsSuggestedBySemanticDiscovery;
    
    // Handle new schema structure
    if (currentData?.patterns && currentData?.components) {
        // New schema: {patterns: [...], components: [...]}
        const updatedData = _.cloneDeep(currentData);
        updatedData.patterns = updatedData.patterns.filter(pattern => pattern.id !== patternId);
        
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
        
        try {
            // Wait for the query to complete
            await queryFulfilled;
        } catch (error) {
            // If request fails, revert to previous state
            dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(currentData));
            console.error('Error removing pattern:', error);
        }
    } else if (Array.isArray(currentData)) {
        // Legacy schema: flat array of patterns
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(
            currentData.filter(pattern => pattern.id !== patternId)
        ));
        
        try {
            // Wait for the query to complete
            await queryFulfilled;
        } catch (error) {
            // If request fails, revert to previous state
            dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(currentData));
            console.error('Error removing pattern:', error);
        }
    }
}

export const handleBatchDeletePatternOnQueryStarted = async ({ patternIds }, { dispatch, getState }) => {
    const { assignment } = getState();
    const currentData = assignment.descriptionsSuggestedBySemanticDiscovery;
    
    const updatedData = _.cloneDeep(currentData);
    updatedData.patterns = updatedData.patterns.filter(pattern => !patternIds.includes(pattern.id));
    dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
}

/**
 * Handler for optimistic creation of discovery components
 * @param {Object} param0 Query parameters
 * @param {Object} param1 API utilities
 */
export const handleCreateComponentOnQueryStarted = async ({ _token, _name, _order }, { dispatch, getState, queryFulfilled }) => {
    const { assignment } = getState();
    const currentData = assignment.descriptionsSuggestedBySemanticDiscovery;
    
    try {
        // Wait for the query to complete and get the result
        const { data: newComponent } = await queryFulfilled;
        
        // Update state with the new component from server response
        const updatedData = _.cloneDeep(currentData);
        if (!updatedData.components) {
            updatedData.components = [];
        }
        updatedData.components.push(newComponent);
        
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
    } catch (error) {
        console.error('Error creating component:', error);
        // No need to revert since we didn't do optimistic update
    }
}

/**
 * Handler for optimistic update of discovery components
 * @param {Object} param0 Query parameters
 * @param {Object} param1 API utilities
 */
export const handleUpdateComponentOnQueryStarted = async ({ componentId, name, order }, { dispatch, getState, queryFulfilled }) => {
    const { assignment } = getState();
    const currentData = assignment.descriptionsSuggestedBySemanticDiscovery;
    
    // Optimistically update the component
    const updatedData = _.cloneDeep(currentData);
    const componentToUpdate = updatedData.components?.find(c => c.id === componentId);
    
    if (componentToUpdate) {
        const originalName = componentToUpdate.name;
        const originalOrder = componentToUpdate.order;
        
        // Apply optimistic update
        componentToUpdate.name = name;
        if (order !== undefined) componentToUpdate.order = order;
        
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
        
        try {
            // Wait for the query to complete
            await queryFulfilled;
        } catch (error) {
            // Revert on error
            componentToUpdate.name = originalName;
            componentToUpdate.order = originalOrder;
            dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
            console.error('Error updating component:', error);
        }
    }
}

/**
 * Handler for optimistic deletion of discovery components
 * @param {Object} param0 Query parameters
 * @param {Object} param1 API utilities
 */
export const handleDeleteComponentOnQueryStarted = async ({ componentId }, { dispatch, getState, queryFulfilled }) => {
    const { assignment } = getState();
    const currentData = assignment.descriptionsSuggestedBySemanticDiscovery;
    
    // Optimistically remove the component
    const updatedData = _.cloneDeep(currentData);
    const originalComponents = updatedData.components;
    updatedData.components = updatedData.components?.filter(c => c.id !== componentId) || [];
    
    dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
    
    try {
        // Wait for the query to complete
        await queryFulfilled;
    } catch (error) {
        // Revert on error
        updatedData.components = originalComponents;
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(updatedData));
        console.error('Error deleting component:', error);
    }
}
